/**
 * Free Gift Updater
 *
 * Architecture:
 * - Single reconcile loop (serialized) to avoid overlapping add/remove races.
 * - Cart is the source of truth for gift presence.
 * - Liquid-rendered qualifying IDs are the source of truth for eligibility.
 * - Auto-add suppression is persisted by non-gift cart signature after auto-remove,
 *   so discount recalculation/morph churn cannot immediately re-add the same gift.
 */

import { ThemeEvents, CartAddEvent, CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const INIT_FLAG = '__mmFreeGiftUpdaterInitialized';
const LOG_PREFIX = '[FreeGift]';
const MAX_AUTO_ADD_RETRIES = 2;
const AUTO_ADD_RETRY_DELAY = 600;

if (!window.__giftLog) window.__giftLog = [];

const state = (window.__mmFreeGiftState = window.__mmFreeGiftState || {
  reconciling: false,
  reconcileQueued: false,
  removeSuppressions: new Map(), // productId -> nonGiftSignature at time of removal
  autoAddAttempted: new Set(), // `${productId}:${nonGiftSignature}`
  systemMutationInFlight: 0,
});

function _dbg(...args) {
  const entry = {
    t: new Date().toISOString(),
    m: args
      .map((a) => (typeof a === 'object' ? JSON.stringify(a) : String(a)))
      .join(' '),
  };
  window.__giftLog.push(entry);
  if (window.__giftLog.length > 300) window.__giftLog.shift();
}

function log(...args) {
  _dbg(...args);
  console.log(LOG_PREFIX, ...args);
}

function warn(...args) {
  _dbg('WARN', ...args);
  console.warn(LOG_PREFIX, ...args);
}

function getSectionsToUpdate() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids);
}

function getQualifyingGiftProductIds() {
  const el = document.querySelector('[data-gift-qualifying-ids]');
  const raw = el?.getAttribute('data-gift-qualifying-ids') || '';
  return raw
    .split(',')
    .map((s) => String(s).trim())
    .filter(Boolean);
}

function nonGiftSignatureFromDom(rows) {
  const parts = rows
    .filter((row) => !row.isGift)
    .map((row) => `${row.variantId}:${row.quantity}:${row.finalLinePrice}`)
    .sort();
  return parts.join('|');
}

function getDomCartRows() {
  return Array.from(document.querySelectorAll('tr.cart-items__table-row[data-key]'))
    .map((el) => {
      if (!(el instanceof HTMLElement)) return null;
      const key = String(el.dataset.key || '').trim();
      if (!key) return null;

      const isGift = String(el.dataset.isGift || '').toLowerCase() === 'true';
      return {
        el,
        key,
        productId: String(el.dataset.productId || '').trim(),
        variantId: String(el.dataset.variantId || '').trim(),
        quantity: Number(el.dataset.quantity || 0) || 0,
        finalLinePrice: Number(el.dataset.finalLinePrice || 0) || 0,
        isGift,
      };
    })
    .filter(Boolean);
}

function hideUnqualifiedGiftRowsInstantly(qualifyingIds) {
  const rows = getDomCartRows();
  const toHide = rows.filter((row) => row.isGift && !qualifyingIds.has(row.productId));

  toHide.forEach((row) => {
    row.el.style.display = 'none';
  });

  return toHide;
}

async function fetchCart() {
  const res = await fetch('/cart.js', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  return await res.json();
}

async function addGiftToCart({ variantId, productName, source = 'manual' }) {
  state.systemMutationInFlight += 1;

  const formData = new FormData();
  formData.append('id', String(variantId));
  formData.append('quantity', '1');
  formData.append('properties[_gift]', 'true');

  const sections = getSectionsToUpdate();
  if (sections.length) formData.append('sections', sections.join(','));
  formData.append('sections_url', window.location.pathname);

  const fetchCfg = fetchConfig('javascript', { body: formData });

  try {
    log(`Adding gift: ${productName} (${variantId}) source=${source}`);

    const res = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'application/json',
      },
    });

    const data = await res.json();
    if (data?.status) {
      throw new Error(data?.description || data?.message || `Could not add ${productName || 'gift'}`);
    }

    let totalItemCount = 1;
    try {
      const cartData = await fetchCart();
      totalItemCount = cartData?.item_count || 1;
    } catch (_) {
      /* noop */
    }

    document.dispatchEvent(
      new CartAddEvent({}, String(variantId), {
        source: `free-gift-updater:${source}`,
        itemCount: totalItemCount,
        sections: data.sections,
      })
    );
  } finally {
    window.setTimeout(() => {
      state.systemMutationInFlight = Math.max(0, state.systemMutationInFlight - 1);
    }, 500);
  }
}

async function removeGiftLineItems(items) {
  if (!items.length) return;

  state.systemMutationInFlight += 1;
  const sections = getSectionsToUpdate();
  let lastResponse = null;

  try {
    for (const item of items) {
      // Remove one unit, not the whole line: if a customer bumped a charged
      // gift-flagged line to qty 2+, the extra paid units survive.
      const newQty = Math.max(0, (Number(item.quantity) || 1) - 1);
      const body = { id: item.key, quantity: newQty, sections_url: window.location.pathname };
      if (sections.length) body.sections = sections;

      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(body),
        });
        lastResponse = await res.json();
        log('Removed unqualified gift', item.product_id, item.title);
      } catch (err) {
        warn('Failed to remove gift', item.product_id, err);
      }
    }

    if (lastResponse) {
      document.dispatchEvent(
        new CartUpdateEvent(lastResponse, 'free-gift-updater-remove', {
          source: 'free-gift-updater:remove',
          itemCount: lastResponse?.item_count,
          sections: lastResponse?.sections,
        })
      );
    }
  } finally {
    // Keep mutation lock briefly after dispatch so morph-triggered cart:update
    // does not immediately re-enter reconcile while DOM is still settling.
    window.setTimeout(() => {
      state.systemMutationInFlight = Math.max(0, state.systemMutationInFlight - 1);
    }, 900);
  }
}

async function maybeAutoAddFromDom(cartProductIds, signature) {
  const gifts = document.querySelectorAll('[data-cart-gift][data-auto-add="true"]');
  if (!gifts.length) return;

  for (const container of gifts) {
    if (!(container instanceof HTMLElement)) continue;

    const giftProductId = String(container.dataset.productId || '');
    const suppressedSig = state.removeSuppressions.get(giftProductId);
    if (suppressedSig && suppressedSig === signature) {
      log(`Auto-add suppressed for product ${giftProductId} (same non-gift cart signature)`);
      continue;
    }
    if (suppressedSig && suppressedSig !== signature) {
      state.removeSuppressions.delete(giftProductId);
    }

    if (cartProductIds.has(giftProductId)) continue;

    const attemptKey = `${giftProductId}:${signature}`;
    if (state.autoAddAttempted.has(attemptKey)) continue;

    const btn = container.querySelector('.cart-gift__add-btn');
    const msg = container.querySelector('.cart-gift__auto-add-message');

    const variantId = Number(btn?.dataset?.variantId);
    const productName = btn?.dataset?.productName || 'gift';

    if (!Number.isFinite(variantId)) {
      warn('Auto-add invalid variant ID', btn?.dataset?.variantId);
      continue;
    }

    state.autoAddAttempted.add(attemptKey);

    let success = false;
    for (let attempt = 1; attempt <= MAX_AUTO_ADD_RETRIES; attempt++) {
      try {
        await addGiftToCart({ variantId, productName, source: 'auto' });
        success = true;
        break;
      } catch (err) {
        warn(`Auto-add attempt ${attempt}/${MAX_AUTO_ADD_RETRIES} failed`, err?.message || err);
        if (attempt < MAX_AUTO_ADD_RETRIES) {
          await new Promise((r) => setTimeout(r, AUTO_ADD_RETRY_DELAY * attempt));
        }
      }
    }

    if (!success) {
      if (msg instanceof HTMLElement) msg.style.display = 'none';
      if (btn instanceof HTMLElement) btn.classList.remove('cart-gift__add-btn--hidden');
    }
  }
}

function handleSwatchClick(e) {
  const swatch = e.target.closest('.cart-gift__swatch');
  if (!swatch || swatch.disabled) return;

  const container = swatch.closest('[data-cart-gift]');
  if (!container) return;

  container.querySelectorAll('.cart-gift__swatch').forEach((s) => s.classList.remove('cart-gift__swatch--active'));
  swatch.classList.add('cart-gift__swatch--active');

  const addBtn = container.querySelector('.cart-gift__add-btn');
  if (addBtn && swatch.dataset.variantId) addBtn.dataset.variantId = swatch.dataset.variantId;
}

async function handleAddButtonClick(e) {
  const btn = e.target.closest('.cart-gift__add-btn');
  if (!btn || btn.disabled || btn.classList.contains('cart-gift__add-btn--hidden')) return;

  const variantId = Number(btn.dataset.variantId);
  const productName = btn.dataset.productName;

  if (!Number.isFinite(variantId)) {
    warn('Invalid variant ID for manual add', btn.dataset.variantId);
    return;
  }

  btn.disabled = true;
  const originalText = btn.textContent;
  btn.textContent = 'Adding...';

  try {
    await addGiftToCart({ variantId, productName, source: 'button' });
  } catch (err) {
    warn('Manual add failed', err);
    btn.disabled = false;
    btn.textContent = originalText;
  }
}

function applyGiftAddedFadeOption() {
  const wrap = document.querySelector('[data-cart-gifts]');
  if (!(wrap instanceof HTMLElement)) return;

  const enabled = String(wrap.dataset.giftIndicatorFade || '').toLowerCase() === 'true';
  if (!enabled) return;

  const seconds = Number(wrap.dataset.giftIndicatorFadeDuration || 0);
  const durMs = Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 4000;

  document.querySelectorAll('.cart-gift--added').forEach((el) => {
    if (!(el instanceof HTMLElement) || el.dataset.faded === 'true') return;
    el.dataset.faded = 'true';

    window.setTimeout(() => {
      el.classList.add('is-fading');
      requestAnimationFrame(() => el.classList.add('is-hidden'));
    }, durMs);
  });
}

async function reconcile() {
  if (state.reconciling) {
    state.reconcileQueued = true;
    return;
  }

  state.reconciling = true;

  try {
    const qualifyingEl = document.querySelector('[data-gift-qualifying-ids]');
    if (!qualifyingEl) return;

    const qualifyingIds = new Set(getQualifyingGiftProductIds());
    const domRows = getDomCartRows();
    const signature = nonGiftSignatureFromDom(domRows);

    // Prong 1: immediate visual cleanup from already-morphed DOM
    const domUnqualifiedGiftRows = hideUnqualifiedGiftRowsInstantly(qualifyingIds);

    if (domUnqualifiedGiftRows.length) {
      domUnqualifiedGiftRows.forEach((row) => {
        state.removeSuppressions.set(row.productId, signature);
      });

      const toRemove = domUnqualifiedGiftRows.map((row) => ({
        key: row.key,
        product_id: row.productId,
        title: 'Gift',
        quantity: row.quantity,
      }));

      await removeGiftLineItems(toRemove);
      return;
    }

    if (state.systemMutationInFlight > 0) {
      return;
    }

    // Prong 2: verify real cart gifts after giving section morphs more wall-clock time.
    // Re-read qualifying IDs AFTER /cart.js returns so we compare against settled DOM.
    let cartData = null;
    try {
      cartData = await fetchCart();
    } catch (err) {
      warn('Failed to fetch /cart.js during reconcile', err);
    }

    if (cartData?.items?.length) {
      const qualifyingIdsAfterFetch = new Set(getQualifyingGiftProductIds());
      const cartGiftItems = cartData.items.filter((item) => item?.properties?._gift);
      const unqualifiedGiftItems = cartGiftItems.filter(
        (item) => !qualifyingIdsAfterFetch.has(String(item.product_id || ''))
      );

      if (unqualifiedGiftItems.length) {
        unqualifiedGiftItems.forEach((item) => {
          state.removeSuppressions.set(String(item.product_id || ''), signature);
        });

        const toRemove = unqualifiedGiftItems.map((item) => ({
          key: item.key,
          product_id: String(item.product_id || ''),
          title: item.title || 'Gift',
          quantity: item.quantity,
        }));

        await removeGiftLineItems(toRemove);
        return;
      }
    }

    // Prong 3: auto-add checks still use DOM product presence (fast, no extra API work).
    const cartProductIds = new Set(domRows.map((row) => row.productId));
    await maybeAutoAddFromDom(cartProductIds, signature);
  } catch (err) {
    warn('Reconcile error', err);
  } finally {
    applyGiftAddedFadeOption();

    // Carousel ladders: slide the claimable (unlocked) card into view.
    document.querySelectorAll('.gift-ladder--carousel').forEach((ladder) => {
      const target = ladder.querySelector('.gift-ladder__row--unlocked');
      if (target instanceof HTMLElement && ladder instanceof HTMLElement) {
        ladder.scrollTo({ left: Math.max(0, target.offsetLeft - ladder.offsetLeft - 8), behavior: 'smooth' });
      }
    });

    state.reconciling = false;

    if (state.reconcileQueued) {
      state.reconcileQueued = false;
      window.setTimeout(reconcile, 120);
    }
  }
}

function scheduleReconcile() {
  clearTimeout(scheduleReconcile._t);
  scheduleReconcile._t = setTimeout(reconcile, 200);
}

export function initFreeGiftUpdater() {
  if (window[INIT_FLAG]) return;
  window[INIT_FLAG] = true;

  log('Initializing free gift updater');

  document.addEventListener('click', (e) => {
    handleSwatchClick(e);
    handleAddButtonClick(e);
  });

  document.addEventListener(ThemeEvents.cartUpdate, scheduleReconcile);
  document.addEventListener(ThemeEvents.discountUpdate, scheduleReconcile);

  setTimeout(scheduleReconcile, 250);
}

initFreeGiftUpdater();
